import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { Rows } from 'base/ui/layout';
import { TickRow } from 'base/ui/tick_row';
import { TextRegularLight } from 'base/ui/typography';
import {
  authRouteToUrl,
  getCurrentLocationForRedirect,
} from 'blocks/auth/routes';
import { AccessLevel } from 'blocks/blocks_service';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

export function createAuthDialogs(
  accessLevel: AccessLevel,
  disableUnloadConfirmation: () => Promise<void>,
) {
  const modeBox = observable.box<'wall' | 'wall-paid' | undefined>();
  const close = () => {
    modeBox.set(undefined);
  };

  return {
    openAuthWall: () => modeBox.set('wall'),
    openAuthThenUpgradeWall: () => {
      modeBox.set('wall-paid');
    },
    DialogLayer: observer(() => {
      const mode = modeBox.get();
      if (mode == null) return null;
      return (
        <AuthWallDialog
          requirePaid={mode === 'wall-paid'}
          onClose={close}
          disableUnloadConfirmation={disableUnloadConfirmation}
        />
      );
    }),
    headerButton:
      accessLevel >= AccessLevel.OWNER ? (
        <SaveThenNavigateButton
          variant="primary"
          href={authRouteToUrl({
            path: 'signup',
            context: { redirect: getCurrentLocationForRedirect() },
          })}
          label="Sign up"
          disableUnloadConfirmation={disableUnloadConfirmation}
        />
      ) : undefined,
  };
}

export function AuthWallDialog({
  requirePaid,
  onClose,
  disableUnloadConfirmation,
}: {
  requirePaid: boolean;
  onClose: () => void;
  disableUnloadConfirmation: () => Promise<void>;
}) {
  const anAccount = requirePaid ? 'a paid account' : 'an account';
  const features = requirePaid
    ? [
        `View all wireframe pages`,
        `Export wireframe to Figma and Webflow`,
        `Create more than 1 project`,
      ]
    : [
        `Save your projects`,
        `Unlimited AI usage`,
        `Export to Figma, CSV and TXT`,
        `Upload images`,
        `Share with others`,
      ];
  return (
    <Dialog
      title={`You need ${anAccount}`}
      onClose={onClose}
      buttons={
        <>
          <SaveThenNavigateButton
            size="large"
            label="Log in"
            href={authRouteToUrl({
              path: 'login',
              context: { redirect: getCurrentLocationForRedirect() },
            })}
            disableUnloadConfirmation={disableUnloadConfirmation}
          />
          <SaveThenNavigateButton
            size="large"
            variant="primary"
            label="Sign up"
            href={authRouteToUrl({
              path: 'signup',
              context: { redirect: getCurrentLocationForRedirect() },
            })}
            disableUnloadConfirmation={disableUnloadConfirmation}
          />
        </>
      }
    >
      <TextRegularLight>
        You need {anAccount} to unlock the following features:
      </TextRegularLight>
      <Rows spacing={8}>
        {features.map((feature) => (
          <TickRow key={feature}>{feature}</TickRow>
        ))}
      </Rows>
    </Dialog>
  );
}

export function SaveThenNavigateButton({
  href,
  disableUnloadConfirmation,
  ...buttonProps
}: {
  href: string;
  disableUnloadConfirmation: () => Promise<void>;
} & React.ComponentProps<typeof Button>) {
  const [loading, setLoading] = React.useState(false);

  function onClick(e: React.MouseEvent) {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    disableUnloadConfirmation()
      .then(() => {
        window.location.href = href;
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <Button {...buttonProps} href={href} onClick={onClick} loading={loading} />
  );
}
